/**
 * CallFlow Debug Tab Component
 *
 * Shows detailed queue state, execution logs, and worker status for debugging.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import { Loader2, RefreshCw, AlertCircle, CheckCircle, Clock, Activity } from 'lucide-react';
import { supabase } from '@/supabase/supabase';

interface CallFlowDebugTabProps {
  organizationId: string;
  callflowId: string;
}

interface QueueState {
  callflow_id: string;
  is_running: boolean;
  worker_id?: string;
  started_at?: string;
  last_heartbeat?: string;
  leads_processed: number;
  updated_at: string;
}

interface ExecutionLog {
  id: string;
  callflow_id: string;
  event_type: string;
  message?: string;
  details?: Record<string, any>;
  created_at: string;
  callflow_lead_id?: string;
  call_log_id?: string;
  phone_number_id?: string;
}

export function CallFlowDebugTab({ callflowId, organizationId }: CallFlowDebugTabProps) {
  const [queueState, setQueueState] = useState<QueueState | null>(null);
  const [executionLogs, setExecutionLogs] = useState<ExecutionLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showHeartbeat, setShowHeartbeat] = useState(false);

  useEffect(() => {
    fetchDebugData();

    // Auto-refresh every 3 seconds if enabled
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchDebugData();
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callflowId, autoRefresh]);

  const fetchDebugData = async () => {
    try {
      // Fetch queue state
      const { data: queueData, error: queueError } = await supabase
        .from('callflow_queue_state')
        .select('*')
        .eq('callflow_id', callflowId)
        .single();

      if (queueError && queueError.code !== 'PGRST116') {
        console.error('Queue state error:', queueError);
      } else {
        setQueueState(queueData);
      }

      // Fetch recent execution logs (last 50)
      const { data: logsData, error: logsError } = await supabase
        .from('callflow_execution_logs')
        .select('*')
        .eq('callflow_id', callflowId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) {
        console.error('Logs error:', logsError);
      } else {
        setExecutionLogs(logsData || []);
      }
    } catch (error) {
      console.error('Failed to fetch debug data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load debug information',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getEventBadge = (eventType: string) => {
    const eventStyles: Record<string, { bg: string; icon: React.ReactNode }> = {
      started: { bg: 'bg-secondary text-foreground', icon: <Activity className="h-3 w-3" /> },
      paused: { bg: 'bg-mid-bg text-mid', icon: <Clock className="h-3 w-3" /> },
      resumed: { bg: 'bg-good-bg text-good', icon: <CheckCircle className="h-3 w-3" /> },
      completed: { bg: 'bg-good-bg text-good', icon: <CheckCircle className="h-3 w-3" /> },
      cancelled: { bg: 'bg-danger-bg text-danger', icon: <AlertCircle className="h-3 w-3" /> },
      call_initiated: { bg: 'bg-secondary text-foreground', icon: <Activity className="h-3 w-3" /> },
      call_completed: { bg: 'bg-good-bg text-good', icon: <CheckCircle className="h-3 w-3" /> },
      call_failed: { bg: 'bg-danger-bg text-danger', icon: <AlertCircle className="h-3 w-3" /> },
      lead_queued: { bg: 'bg-secondary text-foreground', icon: <Clock className="h-3 w-3" /> },
      phone_selected: { bg: 'bg-secondary text-foreground', icon: <Activity className="h-3 w-3" /> },
      retry_scheduled: { bg: 'bg-mid-bg text-mid', icon: <Clock className="h-3 w-3" /> },
      rate_limit_hit: { bg: 'bg-mid-bg text-mid', icon: <AlertCircle className="h-3 w-3" /> },
      concurrency_limit_hit: { bg: 'bg-mid-bg text-mid', icon: <AlertCircle className="h-3 w-3" /> },
      worker_heartbeat: { bg: 'bg-muted text-foreground', icon: <Activity className="h-3 w-3" /> },
      error: { bg: 'bg-danger-bg text-danger', icon: <AlertCircle className="h-3 w-3" /> },
    };

    const style = eventStyles[eventType] || { bg: 'bg-muted text-foreground', icon: null };

    return (
      <Badge className={`${style.bg} flex items-center gap-1`}>
        {style.icon}
        {eventType.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const isWorkerHealthy = () => {
    if (!queueState || !queueState.is_running || !queueState.last_heartbeat) {
      return false;
    }

    const lastHeartbeat = new Date(queueState.last_heartbeat).getTime();
    const now = Date.now();
    const diffSeconds = (now - lastHeartbeat) / 1000;

    // Worker is considered healthy if heartbeat is within last 30 seconds
    return diffSeconds < 30;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-faint" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Debug & System Logs</h3>
        <div className="flex gap-2">
          <Button
            variant={showHeartbeat ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHeartbeat(!showHeartbeat)}
          >
            {showHeartbeat ? 'Hide' : 'Show'} Heartbeat
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Disable' : 'Enable'} Auto-refresh
          </Button>
          <Button variant="outline" size="sm" onClick={fetchDebugData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Worker Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Worker Status</CardTitle>
          </CardHeader>
          <CardContent>
            {queueState ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {isWorkerHealthy() ? (
                    <>
                      <div className="h-2 w-2 rounded-full bg-good animate-pulse" />
                      <span className="text-sm font-medium text-good">Running</span>
                    </>
                  ) : queueState.is_running ? (
                    <>
                      <div className="h-2 w-2 rounded-full bg-mid" />
                      <span className="text-sm font-medium text-mid">Stale</span>
                    </>
                  ) : (
                    <>
                      <div className="h-2 w-2 rounded-full bg-muted" />
                      <span className="text-sm font-medium text-ink-faint">Stopped</span>
                    </>
                  )}
                </div>
                {queueState.worker_id && (
                  <p className="text-xs text-ink-faint">
                    Worker: {queueState.worker_id.substring(0, 8)}...
                  </p>
                )}
                {queueState.last_heartbeat && (
                  <p className="text-xs text-ink-faint">
                    Last heartbeat:{' '}
                    {Math.floor(
                      (Date.now() - new Date(queueState.last_heartbeat).getTime()) / 1000
                    )}
                    s ago
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-danger" />
                <span className="text-sm text-danger">No queue state</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Queue Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Queue Info</CardTitle>
          </CardHeader>
          <CardContent>
            {queueState ? (
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-ink-faint">Leads processed:</span>{' '}
                  <span className="font-semibold">{queueState.leads_processed}</span>
                </p>
                {queueState.started_at && (
                  <p className="text-sm text-ink-faint">
                    Started: {new Date(queueState.started_at).toLocaleString()}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-ink-faint">Queue not initialized</p>
            )}
          </CardContent>
        </Card>

        {/* Log Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <p className="text-sm">
                <span className="text-ink-faint">Total events:</span>{' '}
                <span className="font-semibold">{executionLogs.length}</span>
              </p>
              <p className="text-sm">
                <span className="text-ink-faint">Errors:</span>{' '}
                <span className="font-semibold text-danger">
                  {executionLogs.filter((l) => l.event_type === 'error').length}
                </span>
              </p>
              <p className="text-sm">
                <span className="text-ink-faint">Calls initiated:</span>{' '}
                <span className="font-semibold text-good">
                  {executionLogs.filter((l) => l.event_type === 'call_initiated').length}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diagnostic Messages */}
      {!queueState && (
        <Card className="border-mid-line/50 bg-mid-bg/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-mid flex-shrink-0" />
              <div>
                <p className="font-medium text-mid">Queue Not Initialized</p>
                <p className="text-sm text-mid/80 mt-1">
                  The CallFlow queue state doesn't exist. This means the CallFlow has never been
                  started, or the worker hasn't created the queue entry yet.
                </p>
                <p className="text-sm text-mid/80 mt-2">
                  <strong>Next steps:</strong> Make sure the FastAPI backend is running and try
                  starting the CallFlow.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {queueState && !isWorkerHealthy() && queueState.is_running && (
        <Card className="border-danger-line/50 bg-danger-bg/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-danger flex-shrink-0" />
              <div>
                <p className="font-medium text-danger">Worker Appears Stale</p>
                <p className="text-sm text-danger/80 mt-1">
                  The worker is marked as running, but the last heartbeat was more than 30 seconds
                  ago. The worker may have crashed or been stopped.
                </p>
                <p className="text-sm text-danger/80 mt-2">
                  <strong>Next steps:</strong> Check that the Python worker is running. Restart it
                  if needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {executionLogs.length === 0 && (
        <Card className="border-border/50 bg-secondary/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-foreground flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">No Execution Logs Found</p>
                <p className="text-sm text-foreground/80 mt-1">
                  There are no execution logs for this CallFlow yet. This could mean:
                </p>
                <ul className="list-disc list-inside text-sm text-foreground/80 mt-2 space-y-1">
                  <li>The CallFlow hasn't been started yet</li>
                  <li>The FastAPI backend isn't running</li>
                  <li>The worker hasn't processed any leads yet</li>
                  <li>There was an error before logging could begin</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execution Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Execution Logs (Last 50 events)
            {!showHeartbeat && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                (heartbeat hidden)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {executionLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executionLogs
                    .filter((log) => showHeartbeat || log.event_type !== 'worker_heartbeat')
                    .map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{getEventBadge(log.event_type)}</TableCell>
                      <TableCell className="max-w-md">
                        <p className="text-sm truncate">{log.message || '-'}</p>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-foreground hover:text-foreground">
                              View JSON
                            </summary>
                            <pre className="mt-2 p-2 bg-muted border border-border rounded overflow-x-auto text-ink-faint">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-ink-faint text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No execution logs yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
